import { z } from 'zod'

// Schema for block content based on type
const videoContentSchema = z.object({
  duration: z.number(),
  videoUrl: z.string(),
  resources: z.array(z.string()).optional(),
  transcript: z.string().optional(),
})

const textContentSchema = z.object({
  html: z.string(),
  wordCount: z.number(),
  attachments: z.array(z.object({
    url: z.string(),
    name: z.string(),
    type: z.string(),
  })).optional(),
  readingTime: z.number(),
})

const assignmentContentSchema = z.object({
  hints: z.array(z.string()).optional(),
  instructions: z.string(),
  expectedOutput: z.string().optional(),
})

const imageContentSchema = z.object({
  alt: z.string(),
  caption: z.string(),
  imageUrl: z.string(),
  resources: z.array(z.string()).optional(),
})

const downloadContentSchema = z.object({
  files: z.array(z.object({
    url: z.string(),
    name: z.string(),
    size: z.string(),
    type: z.string(),
  })),
  instructions: z.string(),
})

// Union type for different content types
const blockContentSchema = z.union([
  videoContentSchema,
  textContentSchema,
  assignmentContentSchema,
  imageContentSchema,
  downloadContentSchema,
  z.record(z.string(), z.any()) // Fallback for unknown content types
])

// Schema for block progress (optional, only if user is authenticated)
const blockProgressSchema = z.object({
  status: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED']),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  timeSpent: z.number().optional(), // Time spent in seconds
  score: z.number().nullable().optional(),
  attempts: z.number().optional(),
}).optional()

// Schema for content blocks
const blockSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  type: z.enum(['VIDEO', 'TEXT', 'ASSIGNMENT', 'IMAGE', 'DOWNLOAD', 'DOCUMENT']),
  content: blockContentSchema,
  order: z.number(),
  duration: z.number(), // Duration in minutes
  isPublished: z.boolean(),
  lessonId: z.string(), // Blocks belong to lessons, not modules directly
  moduleId: z.string().optional(), // Kept for backward compatibility
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  // Progress fields (optional, only if user is authenticated)
  progress: blockProgressSchema,
  isCompleted: z.boolean().optional(),
  isStarted: z.boolean().optional(),
})

// Schema for course info (nested in module)
const courseInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
})

// Schema for creator info (nested in module)
const creatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: z.string().nullable(),
})

// Schema for block count
const blockCountSchema = z.object({
  blocks: z.number(),
})

// Schema for quiz attempt
const quizAttemptSchema = z.object({
  id: z.string(),
  score: z.number(),
  totalQuestions: z.number(),
  correctAnswers: z.number(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  passed: z.boolean(),
  timeSpent: z.number().optional(),
}).nullable().optional()

// Schema for quiz progress (optional, only if user is authenticated)
const quizProgressSchema = z.object({
  quiz: z.object({
    id: z.string(),
    title: z.string(),
    passingScore: z.number(),
  }).nullable().optional(),
  attempts: z.array(z.object({
    id: z.string(),
    score: z.number(),
    totalQuestions: z.number(),
    correctAnswers: z.number(),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    passed: z.boolean(),
    timeSpent: z.number().optional(),
  })).nullable().optional(),
  totalAttempts: z.number().nullable().optional(),
  bestAttempt: quizAttemptSchema,
  lastPassedAttempt: quizAttemptSchema,
  isPassed: z.boolean().nullable().optional(),
  canRetake: z.boolean().nullable().optional(),
  remainingAttempts: z.number().nullable().optional(),
}).nullable().optional()

// Schema for module progress (optional, only if user is authenticated)
const moduleProgressSchema = z.object({
  moduleProgress: z.number(), // Percentage
  completedBlocks: z.number(),
  totalBlocks: z.number(),
  isCompleted: z.boolean(),
  allBlocksCompleted: z.boolean(),
}).optional()

// Schema for lesson (minimal, nested in module)
const lessonSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  order: z.number(),
  duration: z.number(),
  isPublished: z.boolean(),
  moduleId: z.string(),
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  blocks: z.array(blockSchema).optional(), // Blocks are optional in module response
  isCompleted: z.boolean().optional(), // Only if authenticated
  isStarted: z.boolean().optional(), // Only if authenticated
})

// Main module schema
export const moduleSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  order: z.number(),
  duration: z.number(), // Duration in minutes
  isPublished: z.boolean(),
  courseId: z.string(),
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  course: courseInfoSchema,
  creator: creatorSchema,
  lessons: z.array(lessonSchema).optional(), // Lessons are now the primary children
  blocks: z.array(blockSchema).optional(), // Blocks are deprecated at module level (kept for backward compatibility)
  _count: blockCountSchema.optional(),
  // Progress fields (optional, only if user is authenticated)
  progress: moduleProgressSchema.optional(),
  quiz: quizProgressSchema.optional(), // Quiz structure: { quiz: {...}, attempts: [...], ... }
})

export type Module = z.infer<typeof moduleSchema>
export type Lesson = z.infer<typeof lessonSchema>
export type Block = z.infer<typeof blockSchema>
export type VideoContent = z.infer<typeof videoContentSchema>
export type TextContent = z.infer<typeof textContentSchema>
export type AssignmentContent = z.infer<typeof assignmentContentSchema>

// Schema for the modules data (after apiClient extracts data.data)
export const modulesApiResponseSchema = z.object({
  modules: z.array(moduleSchema),
  total: z.number(),
  page: z.number(),
  totalPages: z.number(),
})

// Schema for the complete API response (before apiClient processing)
export const fullModulesApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    modules: z.array(moduleSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
  }),
  messages: z.array(z.string()).optional(),
  errors: z.array(z.string()).optional(),
})

export type ModulesApiResponse = z.infer<typeof modulesApiResponseSchema>

// Schema for array of modules (for backward compatibility)
export const modulesArraySchema = z.array(moduleSchema)
